import {
  type ChangeEvent,
  type PointerEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import styles from './DateTimeQuestionAnswerEditor.module.css'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  InputShellControl,
  InputShellRoot,
} from '@/components/inputs/InputShell'
import { useFocus } from 'react-aria'
import { Skeleton, useIsSkeleton } from '@/components/Skeleton'
import { isSafari } from '@/utils/browser-util'

export interface DateTimeQuestionAnswerEditorProps {
  collectTime?: boolean
  value?: Date | null
  isDisabled?: boolean
  isInvalid?: boolean
  isSkeleton?: boolean
  onChange?: (value: Date | null) => unknown
  min?: Date | null
  max?: Date | null
  name?: string
  'aria-describedby'?: string
  'aria-labelledby'?: string
  /**
   * Test ID attribute for the date/time input.
   */
  testId?: string
}

const isRunningInSafari = isSafari()

/**
 * Editor for date-time questions.
 */
export function DateTimeQuestionAnswerEditor({
  collectTime,
  value,
  isDisabled,
  isInvalid,
  onChange,
  isSkeleton,
  name,
  min,
  max,
  'aria-describedby': ariaDescribedby,
  'aria-labelledby': ariaLabelledby,
  testId,
}: DateTimeQuestionAnswerEditorProps) {
  const isSpacious = useIsSpacious()
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState<Date | null>(null)
  const effectiveValue = value === undefined ? internalValue : value
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)

  isSkeleton = useIsSkeleton(isSkeleton)

  const isShowingCustomPlaceholder =
    isRunningInSafari && !effectiveValue && !isFocused

  const formattedValue = useMemo(() => {
    if (!effectiveValue) {
      return ''
    }
    return collectTime
      ? formatDateTimeLocal(effectiveValue)
      : formatDate(effectiveValue)
  }, [collectTime, effectiveValue])

  const formattedMin = useMemo(() => {
    if (!min) {
      return undefined
    }
    return collectTime ? formatDateTimeLocal(min) : formatDate(min)
  }, [collectTime, min])

  const formattedMax = useMemo(() => {
    if (!max) {
      return undefined
    }
    return collectTime ? formatDateTimeLocal(max) : formatDate(max)
  }, [collectTime, max])

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const newDate = inputValue ? new Date(inputValue) : null

      if (!isControlled) {
        setInternalValue(newDate)
      }

      onChange?.(newDate)
    },
    [isControlled, onChange],
  )

  const focusInputOnClick: PointerEventHandler = useCallback((event) => {
    const target = event.target as HTMLElement
    if (target.closest('input, button, a')) {
      return
    }

    const input = inputRef.current
    if (!input) {
      return
    }

    requestAnimationFrame(() => {
      input.focus()
      input.showPicker()
    })
  }, [])

  const { focusProps } = useFocus({
    onFocusChange: setIsFocused,
  })

  const isPlaceholder = !isFocused && effectiveValue === null

  const placeholderText = collectTime
    ? 'Select a date and time'
    : 'Select a date'

  return (
    <InputShellRoot
      isFocused={isFocused}
      onPointerDown={focusInputOnClick}
      isDisabled={isDisabled}
      invalid={isInvalid}
    >
      <InputShellControl pad={isSkeleton}>
        <div className={styles.control}>
          {isSkeleton ? (
            <Skeleton
              topCorrection={0}
              height={isSpacious ? 50 : 24}
              width="75%"
            />
          ) : (
            <>
              <input
                name={name}
                disabled={isDisabled}
                ref={inputRef}
                type={collectTime ? 'datetime-local' : 'date'}
                value={formattedValue}
                onChange={handleChange}
                min={formattedMin}
                max={formattedMax}
                placeholder={placeholderText}
                aria-describedby={ariaDescribedby}
                aria-labelledby={ariaLabelledby}
                data-test={testId}
                {...focusProps}
                className={cx(
                  styles.input,
                  isSpacious ? styles.inputSpacious : styles.inputDefault,
                  isPlaceholder && styles.placeholder,
                  isShowingCustomPlaceholder && styles.inputHidden,
                )}
              />
              {isShowingCustomPlaceholder && (
                // Mobile Safari doesn't show the masked input placeholder.
                // Desktop Safari shows the current date.
                // We'll add something to make it look less empty on mobile,
                // and less filled on desktop (don't want to give the user
                // the impression that they don't need to do anything).
                <div
                  // Screen readers will be getting placeholder info from the
                  // input itself.
                  aria-hidden
                  className={cx(
                    styles.placeholderOverlay,
                    isSpacious
                      ? styles.placeholderOverlaySpacious
                      : styles.placeholderOverlayDefault,
                  )}
                >
                  {placeholderText}
                </div>
              )}
            </>
          )}
        </div>
      </InputShellControl>
    </InputShellRoot>
  )
}

/**
 * Formats the date to the `value` needed by `datetime-local` inputs.
 *
 * @param date
 */
function formatDateTimeLocal(date: Date) {
  const tzOffsetMs = date.getTimezoneOffset() * 60000
  return new Date(date.valueOf() - tzOffsetMs).toISOString().slice(0, 16)
}

/**
 * Formats the date to the `value` needed by `date` inputs.
 *
 * @param date
 */
function formatDate(date: Date) {
  return date.toISOString().substring(0, 10)
}
